import { useContext, useEffect, useMemo, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'

import useServer from '../../../api/useServerMain'
import useServerProduct from '../../../api/useServerProducts'
import CurrencyAmountInput from '../../../components/forms/CurrencyAmountInput'
import InlineRadio from '../../../components/forms/InlineRadio'
import TextArea from '../../../components/forms/TextArea'
import Toggle from '../../../components/forms/Toggle'
import AlertContainer from '../../../components/misc/AlertContainer'
import Button from '../../../components/misc/Button'
import GenericTable, {
  DataTableInterface,
} from '../../../components/misc/GenericTable'
import ImageComponent from '../../../components/misc/Images/Image'
import { BtnActions } from '../../../components/misc/MultipleActBtn'
import SpinnerLoading from '../../../components/misc/SpinnerLoading'
import Modal from '../../../components/modals/GenericModal'
import { ServerVariationInterface } from '../../../interfaces/ServerInterfaces'
import { useAppSelector } from '../../../store/hooks'
import { formatCurrency } from '../../../utils/helpers'
import { DetailProductContext } from '../DetailProductContainer'
import { cn } from '@/lib/utils'

//MAIN COMPONENT ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
const Variations = () => {
  const { denyRoles } = useServer()
  const { product } = useContext(DetailProductContext)
  const [detailModalData, setDetailModalData] = useState<number | null>(null)
  const [addModal, setAddModal] = useState(false)

  //Table data -------------------------------------------------------------------------------
  const tableTitles = ['Variación', 'Precio', 'Descripción']
  const tableData: DataTableInterface[] =
    product?.variations.map((item) => ({
      rowId: item.id,
      payload: {
        Variación: (
          <div className="inline-flex items-center gap-2">
            <ImageComponent
              className="h-8 w-8 overflow-hidden rounded-full"
              hash={item.image?.blurHash}
              src={item.image?.src}
            />
            <p>{item.name}</p>
          </div>
        ),
        Precio: item?.price
          ? formatCurrency(item.price.amount, item.price.codeCurrency)
          : '-',
        Descripción: item?.description ?? '-',
      },
    })) ?? []

  const currentVariation: ServerVariationInterface | null = useMemo(() => {
    const current = product?.variations.find(
      (itm) => itm.id === detailModalData
    )
    if (current) {
      return current
    } else {
      return null
    }
  }, [product?.variations, detailModalData])

  const rowAction = (id?: string | number) => {
    if (typeof id === 'number') {
      setDetailModalData(id)
    }
  }

  const actions: BtnActions[] = []

  if (denyRoles(['AUDITOR'])) {
    actions.unshift({
      title: 'Nueva variación',
      action: () => setAddModal(true),
      icon: <PlusIcon className="h-5" />,
    })
  }

  //-------------------------------------------------------------------------------------------------

  return (
    <div
      className={cn(
        'h-[500px] overflow-scroll rounded-md border border-slate-300 p-5 pr-2 scrollbar-thin',
        'scrollbar-thumb-slate-200'
      )}
    >
      <GenericTable
        actions={actions}
        rowAction={rowAction}
        tableData={tableData}
        tableTitles={tableTitles}
      />
      {!!currentVariation && (
        <Modal
          close={() => setDetailModalData(null)}
          size="m"
          state={!!detailModalData}
        >
          <VariationDetail
            close={() => setDetailModalData(null)}
            variation={currentVariation}
          />
        </Modal>
      )}
      {addModal && (
        <Modal close={() => setAddModal(false)} size="m" state={addModal}>
          <AddVariation close={() => setAddModal(false)} />
        </Modal>
      )}
    </div>
  )
}
//+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

//DETAILS COMPONENT ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
interface DetailVariation {
  variation: ServerVariationInterface
  close: () => void
}

const VariationDetail = ({ variation, close }: DetailVariation) => {
  const { denyRoles } = useServer()

  const { handleSubmit, control, formState, watch, unregister } = useForm()
  const { updateVariation, isFetching } = useServerProduct()
  const { deleteVariationProduct, isFetching: fetchingDelete } =
    useServerProduct()
  const { business } = useAppSelector((state) => state.init)
  const { product, updateVariationState, updateStockProductState } =
    useContext(DetailProductContext)
  const [imageModal, setImageModal] = useState(false)

  const currenciesSelector =
    business?.availableCurrencies.map((itm) => itm.code) ?? []

  const [deleteAlert, setDeleteAlert] = useState(false)

  const addCallback = (variation: ServerVariationInterface) => {
    updateVariationState?.(variation)
    close()
  }

  const enablePrice = watch('enablePrice')

  useEffect(() => {
    if (!enablePrice) unregister('price')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enablePrice])

  interface VariationFormData {
    price?: number | { amount?: number; codeCurrency?: string }
    description?: string
    enablePrice?: boolean
  }

  const onSubmit: SubmitHandler<VariationFormData> = (data) => {
    const normalizedData = { ...data }

    // Normalizamos el campo price
    if (typeof data.price === 'number') {
      // Si viene solo el número
      normalizedData.price = {
        amount: data.price,
        codeCurrency: variation.price?.codeCurrency ?? 'USD', // o la que quieras por defecto
      }
    } else if (typeof data.price === 'object') {
      // Si viene objeto incompleto (ej: solo codeCurrency)
      normalizedData.price = {
        amount: data.price.amount ?? variation.price?.amount ?? 0,
        codeCurrency:
          data.price.codeCurrency ?? variation.price?.codeCurrency ?? 'USD',
      }
    }
    if (formState.isDirty)
      //@ts-expect-error:updateVariation(variation.id, normalizedData, addCallback)
      updateVariation(variation.id, normalizedData, addCallback)
    else close()
  }

  const deleteCallback = () => {
    if (updateVariationState) updateVariationState(variation, true)
    if (updateStockProductState)
      updateStockProductState({
        ...product,
        variation:
          product &&
          product.variations.filter((itm) => itm.id !== variation.id),
      })
    close()
  }

  return (
    <div className="">
      <div className="absolute top-5">
        <Button
          action={() => setDeleteAlert(true)}
          color="red-500"
          icon={<TrashIcon className="h-5" />}
          outline
          textColor="red-500"
        />
      </div>
      <h5 className="mt-5 text-lg">{variation.name}</h5>
      <div className="grid h-full grid-cols-10 gap-2 pt-2">
        <div className="group relative col-span-4 h-full rounded-md border border-gray-300 p-1">
          <ImageComponent
            className="h-full w-full"
            hash={variation?.image?.blurHash}
            src={variation?.image?.src}
          />
          <div
            className={cn(
              'absolute left-0 top-0 hidden h-full w-full items-center justify-center bg-gray-500',
              'opacity-60 group-hover:flex'
            )}
          >
            <p
              className="cursor-pointer font-normal text-white underline hover:text-lg"
              onClick={() => setImageModal(true)}
            >
              Cambiar imagen
            </p>
          </div>
        </div>
        <div className="col-span-6 rounded-md border border-gray-300 p-3">
          <form
            className="flex h-full flex-col"
            onSubmit={handleSubmit(onSubmit)}
          >
            <Toggle
              control={control}
              defaultValue={!!variation.price}
              name="enablePrice"
              title="Habilitar precio"
            />
            <div className="flex h-full flex-col">
              {(watch('enablePrice') ?? !!variation.price) && (
                <CurrencyAmountInput
                  control={control}
                  currencies={currenciesSelector}
                  defaultCurrency={variation.price?.codeCurrency}
                  defaultValue={variation.price?.amount}
                  label="Precio"
                  name="price"
                />
              )}
              <TextArea
                control={control}
                defaultValue={variation.description}
                label="Descripción"
                name="description"
              />
            </div>
            <div className="flex justify-end p-3">
              {denyRoles(['AUDITOR']) && (
                <Button
                  color="slate-600"
                  loading={isFetching}
                  name={formState.isDirty ? 'Actualizar' : 'Aceptar'}
                  type="submit"
                />
              )}
            </div>
          </form>
        </div>
      </div>
      {imageModal && (
        <Modal close={() => setImageModal(false)} size="m" state={imageModal}>
          <ChangeImage
            close={() => setImageModal(false)}
            variation={variation}
          />
        </Modal>
      )}

      {deleteAlert && (
        <Modal close={() => setDeleteAlert(false)} state={deleteAlert}>
          <AlertContainer
            loading={fetchingDelete}
            onAction={() =>
              deleteVariationProduct(variation.id, deleteCallback)
            }
            onCancel={() => setDeleteAlert(false)}
            text="Seguro desea continuar?"
            title={`Eliminar variación "${product?.attributes
              ?.map((itm) => itm.value)
              .join(', ')}"`}
          />
        </Modal>
      )}
    </div>
  )
}
//++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

//CHANGE IMAGE++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
interface Image {
  variation: ServerVariationInterface
  close: () => void
}
const ChangeImage = ({ variation, close }: Image) => {
  const { denyRoles } = useServer()

  const { product, updateVariationState, updateStockProductState } =
    useContext(DetailProductContext)
  const { updateVariation, isFetching } = useServerProduct()
  const availablesImages = product?.images ?? []
  const { handleSubmit, setValue, watch } = useForm()

  const updateCallback = (variation: ServerVariationInterface) => {
    const newVariations =
      (product &&
        product.variations.filter((itm) => itm.id !== variation.id)) ??
      []
    if (updateStockProductState)
      updateStockProductState({
        ...product,
        variations: [...newVariations, variation],
      })
    updateVariationState?.(variation)
    close()
  }

  useEffect(() => {
    if (variation?.image) setValue('imageId', variation.image.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variation?.image])

  const onSubmit: SubmitHandler<Record<string, number>> = (data) => {
    updateVariation(variation.id, data, updateCallback)
  }

  const imageId = watch('imageId')
  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="inline-flex w-full items-center justify-between py-3">
          <h5 className="text-lg text-gray-600">
            {availablesImages?.length > 0
              ? 'Seleccione la imagen deseada'
              : 'No hay imágenes disponibles'}
          </h5>
          {imageId && (
            <Button
              action={() => setValue('imageId', null)}
              color="red-500"
              icon={<TrashIcon className="h-5 text-red-500" />}
              name="Eliminar selección"
              outline
              textColor="red-500"
            />
          )}
        </div>

        <div className="absolute right-10 top-5"></div>
        <div className="flex flex-wrap justify-center gap-3 pt-3">
          {availablesImages?.map((item, idx) => (
            <div className="relative h-40 w-40" key={idx}>
              <input
                checked={imageId === item.id}
                className={cn(
                  'h-full w-full rounded-sm border-none bg-transparent checked:bg-none checked:text-transparent',
                  'checked:ring-2 checked:ring-slate-500 active:ring-2 active:ring-slate-500'
                )}
                onChange={() => setValue('imageId', item.id)}
                type="radio"
                value={item.id}
              />
              <ImageComponent
                className="absolute left-0 top-0 -z-10 h-full w-full"
                hash={item?.blurHash}
                src={item?.src}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end py-5">
          {denyRoles(['AUDITOR']) && availablesImages.length > 0 && (
            <Button
              color="slate-600"
              loading={isFetching}
              name="Actualizar"
              type="submit"
            />
          )}
        </div>
      </form>
    </div>
  )
}
//+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

//ADD COMPONENT++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
interface AddInterface {
  close: () => void
}

const AddVariation = ({ close }: AddInterface) => {
  const {
    getProductAttributes,
    addVariation,
    productAttributes,
    isLoading,
    isFetching,
  } = useServerProduct()
  const { product, updateVariationState, updateStockProductState } =
    useContext(DetailProductContext)
  const { handleSubmit, control } = useForm()

  const onSubmit: SubmitHandler<Record<string, string>> = (data) => {
    if (Object.values(data).some((item) => !item)) {
      toast.warn('Debe asignarle un valor a todos los atributos')
    } else {
      if (addVariation && product)
        addVariation(
          product.id,
          data,
          close,
          // @ts-expect-error:updateVariationState
          updateVariationState,
          updateStockProductState
        )
    }
  }

  useEffect(() => {
    if (product && product.id !== undefined) getProductAttributes(product?.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isLoading)
    return (
      <SpinnerLoading className="flex h-48 flex-col items-center justify-center" />
    )

  return (
    <div>
      <h5 className="text-lg font-semibold text-gray-500">Nueva variación</h5>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={`grid grid-cols-2 gap-3 pt-3`}>
          {productAttributes.map((item) => (
            <div
              className="flex flex-col items-center gap-y-2 rounded-md border border-gray-300 p-5"
              key={item.code}
            >
              <h5 className="text-md font-semibold text-gray-500">
                {item.name}
              </h5>
              <div className="flex">
                <InlineRadio
                  control={control}
                  data={item.values.map((val) => ({
                    label: val.value,
                    value: val.value,
                  }))}
                  name={item.code}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end pt-3">
          <Button
            color="slate-600"
            loading={isFetching}
            name="Insertar"
            type="submit"
          />
        </div>
      </form>
    </div>
  )
}
//+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

export default Variations
